import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs';
import { WidgetAdminHandler } from '@widget-admin/services/widget-admin-handler';
import { QueryBuilderConfigProvider } from '@common/filter/query-builder-config-provider';
import { WidgetConfiguration, WidgetConfigurationsService } from '@feature/widget-configs';
import { mapWherePropertiesMatch } from '@common/index';
import {
  InvoiceStatusConfiguration,
  SaveInvoiceStatusWidgetConfigurationRequest,
  invoiceStatusWidgetViewSelectItems
} from '@feature/invoice-status/invoice-status-widget-types';
import { InvoiceStatusWidgetHttpService } from '@feature/invoice-status/services/invoice-status-widget-http.service';
import { getDefaultWidgetConfigAdminForm, addColumnFieldsToAdminForm, addFilterFieldsToAdminForm } from '@feature/widget-admin/services/widget-admin-form';
import { WidgetColumnsHttpService } from '@feature/widget-columns/services/widget-columns-http.service';
import { AdminInvoiceStatusWidgetFilterFieldProvider } from '@feature/invoice-status/filter/admin-invoice-status-widget-filter-field-provider';
import { VatRateService } from '@feature/invoice-upload/services/vat-rates.service';
import { internal_invoice_status, external_invoice_status } from '@feature/invoice-status/invoice-status-widget-types';
import { FormDefinition, FormFieldDefinition } from '@feature/_app_modules/dynamic-forms/dynamic-form';

@Injectable({
  providedIn: 'root'
})
export class InvoiceStatusWidgetAdminHandler implements WidgetAdminHandler {

  constructor(
    private readonly widgetColumnsHttp: WidgetColumnsHttpService,
    private readonly widgetConfigService: WidgetConfigurationsService,
    private readonly invoiceStatusHttp: InvoiceStatusWidgetHttpService,
    private readonly vatRateService: VatRateService,
  ) { }


  private createSaveRequest(selectedConfig: WidgetConfiguration, adminFormValue: any): SaveInvoiceStatusWidgetConfigurationRequest {

    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);
    const saveRequest = {} as SaveInvoiceStatusWidgetConfigurationRequest;

    saveRequest.widgetConfiguration = copyOfSelectedConfig;
    saveRequest.invoiceStatusConfig = {
      DefaultView: adminFormValue.defaultView,
    };

    return saveRequest;
  }

  initialise(): boolean | Observable<boolean> {
    return true;
  }

  getAdminForm(selectedConfig: WidgetConfiguration): FormDefinition | Observable<FormDefinition> {

    const getAllColumns = this.widgetColumnsHttp.getAllColumns(selectedConfig.id);
    const getWidgetConfig = this.invoiceStatusHttp.getWidgetConfiguration(selectedConfig.id);
    const getVatRates = this.vatRateService.getVatRates();

    const all = forkJoin([getAllColumns, getWidgetConfig, getVatRates]).pipe(map(([columns, config, vatRates]) => {

      let formDefinition = getDefaultWidgetConfigAdminForm(selectedConfig);

      const defaultConfig: InvoiceStatusConfiguration = {
        DefaultView: 'InvoiceStatusVolume'
      };

      config = config ? config : defaultConfig;

      formDefinition.fields.push(
        {
          key: 'defaultView',
          label: 'Default Widget View',
          value: config.DefaultView,
          type: 'dropdown',
          options: invoiceStatusWidgetViewSelectItems,
          asyncValidation: [],
        } as FormFieldDefinition);

      formDefinition = addColumnFieldsToAdminForm(selectedConfig, columns, formDefinition);

      const invoiceStatuses = selectedConfig.widgetTypeId == 'MojoInvoiceStatus'
        ? external_invoice_status
        : internal_invoice_status;

      const filterFieldProvider = new AdminInvoiceStatusWidgetFilterFieldProvider(vatRates, invoiceStatuses);
      const queryConfigProvider = new QueryBuilderConfigProvider(filterFieldProvider);
      const queryConfig = queryConfigProvider.getQueryBuilderConfig();

      formDefinition = addFilterFieldsToAdminForm(selectedConfig, queryConfig , formDefinition);

      return formDefinition;

    }));

    return all;

  }

  createCopyFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {
    const saveRequest = this.createSaveRequest(selectedConfig, adminFormValue);
    return this.invoiceStatusHttp.copyWidgetConfiguration(saveRequest);
  }

  saveConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {
    const saveRequest = this.createSaveRequest(selectedConfig, adminFormValue);
    return this.invoiceStatusHttp.saveWidgetConfiguration(saveRequest);
  }

  deleteConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {
    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);
    return this.widgetConfigService.delete(copyOfSelectedConfig).pipe(map(() => true));
  }



}
